import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Megaphone, Sparkles, ArrowRight, Plus, Eye, MousePointer, BadgeCheck, Clock, Building2, AlertCircle, BarChart3, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const STATUS_BADGE = {
  draft:    { label: "Brouillon",       cls: "bg-white/10 text-white/55 border-white/20" },
  pending:  { label: "En validation",   cls: "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/30" },
  paid:     { label: "Active",          cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  refunded: { label: "Remboursée",      cls: "bg-brand-red/15 text-brand-red border-brand-red/30" },
};

const ACC_BADGE = {
  pending:  { label: "Demande en cours",     cls: "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/30", icon: Clock },
  approved: { label: "Compte approuvé",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: BadgeCheck },
  rejected: { label: "Demande non retenue",  cls: "bg-brand-red/15 text-brand-red border-brand-red/30", icon: AlertCircle },
};

export const AdvertiserDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/advertiser");
      setData(data);
      setCompany(data.company || "");
    } catch { /* not logged in */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!user) { navigate("/login?next=/annonceur"); return; }
    load();
    // eslint-disable-next-line
  }, [user]);

  const apply = async () => {
    if (!company.trim()) { toast.error("Nom de l'entreprise requis"); return; }
    setBusy(true);
    try {
      await api.post("/me/become-advertiser", { company: company.trim() });
      toast.success("Demande envoyée. Notre équipe valide sous 24h ✨");
      load();
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="px-6 py-12 text-white/55">Chargement…</div>;

  // Pas encore inscrit comme annonceur
  if (!data?.status) {
    return (
      <div className="px-4 md:px-12 py-12 max-w-3xl mx-auto" data-testid="advertiser-onboarding">
        <Link to="/annoncer" className="text-xs text-white/45 hover:text-white">← Retour à /annoncer</Link>
        <div className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-br from-brand-orange/10 via-transparent to-brand-red/5 p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center"><Megaphone size={22} /></div>
            <div>
              <h1 className="font-display text-3xl font-black">Devenir annonceur</h1>
              <p className="text-sm text-white/60">Crée et lance tes campagnes pub en quelques clics</p>
            </div>
          </div>
          <ul className="text-sm text-white/75 space-y-1.5 mb-5 pl-1">
            <li>✓ Stats temps réel (impressions, clics, CTR)</li>
            <li>✓ Ciblage par rubrique et par pays</li>
            <li>✓ Activation manuelle après paiement (Mvola ou virement)</li>
            <li>✓ Support prioritaire</li>
          </ul>
          <label className="text-[11px] uppercase tracking-widest text-white/45 font-bold block mb-1 inline-flex items-center gap-1"><Building2 size={11} /> Nom de ton entreprise / marque</label>
          <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Ex : Telma, Sakafo Lalao, Festival Donia…"
            className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-orange/60 mb-4"
            data-testid="advertiser-company-input" />
          <button onClick={apply} disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="advertiser-apply-btn">
            {busy ? "Envoi…" : "Demander la validation"} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Statut pending / rejected
  const accBadge = ACC_BADGE[data.status];
  if (data.status !== "approved") {
    return (
      <div className="px-4 md:px-12 py-12 max-w-2xl mx-auto" data-testid="advertiser-status-page">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-widest mb-4 ${accBadge.cls}`}>
            <accBadge.icon size={11} /> {accBadge.label}
          </div>
          <h2 className="font-display text-2xl font-black mb-2">{data.company || "Compte annonceur"}</h2>
          {data.status === "pending" && <p className="text-sm text-white/65">Notre équipe vérifie ta demande. Tu recevras un email dès que c'est validé (sous 24h ouvrées).</p>}
          {data.status === "rejected" && <p className="text-sm text-white/65">Ta demande n'a pas été retenue. Pour toute question : <a className="text-brand-orange hover:underline" href="mailto:contact@voamada.com">contact@voamada.com</a></p>}
        </div>
      </div>
    );
  }

  // Approuvé : dashboard
  const totals = (data.campaigns || []).reduce((acc, c) => {
    acc.imp += +c.impressions || 0; acc.clk += +c.clicks || 0;
    acc.active += c.payment_status === "paid" && +c.active === 1 ? 1 : 0;
    return acc;
  }, { imp: 0, clk: 0, active: 0 });

  return (
    <div className="px-4 md:px-12 py-10 max-w-6xl mx-auto pb-32" data-testid="advertiser-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
        <div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs font-bold uppercase tracking-widest mb-2"><BadgeCheck size={11} /> Annonceur approuvé</span>
          <h1 className="font-display text-3xl md:text-5xl font-black">{data.company}</h1>
          <p className="text-sm text-white/55 mt-1">Bienvenue sur ton portail Voamada Ads</p>
        </div>
        <button onClick={() => navigate("/annonceur/campagnes/nouvelle")} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="new-campaign-btn">
          <Plus size={14} /> Nouvelle campagne
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        <Stat icon={Sparkles} label="Campagnes actives" value={totals.active} accent="emerald" />
        <Stat icon={Eye} label="Impressions cumulées" value={totals.imp.toLocaleString("fr-FR")} />
        <Stat icon={MousePointer} label="Clics cumulés" value={totals.clk.toLocaleString("fr-FR")} />
      </div>

      <h2 className="font-display text-xl font-black mb-3 inline-flex items-center gap-2"><BarChart3 size={18} /> Mes campagnes</h2>

      {data.campaigns?.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
          <p className="text-white/55 mb-3">Aucune campagne pour le moment.</p>
          <button onClick={() => navigate("/annonceur/campagnes/nouvelle")} className="btn-primary inline-flex items-center gap-2 text-sm">
            <Plus size={14} /> Créer ma première campagne
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {data.campaigns.map((c) => {
            const sb = STATUS_BADGE[c.payment_status] || STATUS_BADGE.draft;
            const ctr = c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(2) : "0.00";
            return (
              <div key={c.ad_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 grid md:grid-cols-[1fr_320px] gap-4 items-center" data-testid={`campaign-${c.ad_id}`}>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-sm">{c.title}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest font-bold ${sb.cls}`}>{sb.label}</span>
                    {c.payment_status === "paid" && +c.active === 1 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold uppercase tracking-widest">● Live</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-white/55 flex-wrap">
                    <span>Pack <strong>{c.pack_key}</strong></span>
                    <span>·</span>
                    <span>{c.placement}</span>
                    {c.category && <><span>·</span><span>{c.category}</span></>}
                    {c.payment_amount_ar && <><span>·</span><span className="text-brand-yellow font-bold">{Number(c.payment_amount_ar).toLocaleString("fr-FR")} Ar</span></>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Tile label="Impressions" value={Number(c.impressions || 0).toLocaleString("fr-FR")} />
                  <Tile label="Clics" value={Number(c.clicks || 0).toLocaleString("fr-FR")} />
                  <Tile label="CTR" value={`${ctr}%`} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 p-4 rounded-xl bg-white/[0.02] border border-white/10 text-xs text-white/55 flex items-start gap-2">
        <AlertCircle size={14} className="shrink-0 mt-0.5" />
        <span>Pour modifier ou prolonger une campagne, contacte-nous à <a href="mailto:contact@voamada.com" className="text-brand-orange hover:underline inline-flex items-center gap-1">contact@voamada.com <ExternalLink size={10} /></a></span>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-2xl border p-4 ${accent === "emerald" ? "border-emerald-500/25 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45 font-bold mb-2"><Icon size={11} /> {label}</div>
    <p className="font-display text-3xl font-black tabular-nums">{value}</p>
  </div>
);

const Tile = ({ label, value }) => (
  <div className="rounded-lg bg-black/20 border border-white/10 p-2">
    <p className="font-display text-base font-black tabular-nums">{value}</p>
    <p className="text-[9px] uppercase tracking-widest text-white/45 font-bold">{label}</p>
  </div>
);

export default AdvertiserDashboardPage;
